// ASK-1 — pure query ↔ intent matching for pilot KB ask artifacts.

/** Low-signal tokens stripped before feature-specific matching. */
const GENERIC_QUERY_TOKENS = new Set([
  "how",
  "do",
  "i",
  "use",
  "the",
  "can",
  "you",
  "give",
  "me",
  "a",
  "an",
  "to",
  "what",
  "is",
  "where",
  "of",
  "my",
  "this",
  "that",
  "with",
  "for",
  "in",
  "on",
  "at",
  "from",
  "and",
  "or",
  "tour",
  "walk",
  "through",
  "show",
  "feature",
  "help",
  "does",
  "app",
  "work",
]);

export function normalizeAskQuery(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, " ");
}

export function distinctiveQueryTokens(query: string): string[] {
  return normalizeAskQuery(query)
    .split(" ")
    .filter((t) => t.length > 2 && !GENERIC_QUERY_TOKENS.has(t));
}

/**
 * Higher is better. Zero means no match.
 */
export function scoreAskIntentMatch(query: string, intent: string): number {
  const q = normalizeAskQuery(query);
  const i = normalizeAskQuery(intent);
  if (!q || !i) return 0;
  if (q === i) return 100;
  if (q.includes(i)) return 90;
  if (i.includes(q)) return 85;

  const qTokens = q.split(" ").filter((t) => t.length > 2);
  if (qTokens.length === 0) return 0;
  const matched = qTokens.filter((t) => i.includes(t)).length;
  if (matched === 0) return 0;

  let score = 40 + matched * 15;

  const distinctive = distinctiveQueryTokens(query);
  if (distinctive.length >= 2) {
    const matchedDistinctive = distinctive.filter((t) => i.includes(t)).length;
    if (matchedDistinctive === 0) {
      return Math.min(score, 35);
    }
    if (matchedDistinctive === distinctive.length) {
      score += 10;
    } else {
      score += matchedDistinctive * 5;
    }
  }

  return score;
}

export function bestIntentMatchScore(
  query: string,
  intents: readonly string[],
): number {
  let best = 0;
  for (const intent of intents) {
    const score = scoreAskIntentMatch(query, intent);
    if (score > best) best = score;
  }
  return best;
}
